import { Navigate, useLocation, useSearchParams } from "react-router"

import { Spinner } from "../../components/Spinner"
import { useAccessToken } from "../../stores"
import { useLiabilityInsuranceStore } from "../../stores/useLiabilityInsuranceStore"
import LiabilityInsuranceCoverage from "./LiabilityInsuranceCoverage"
import LiabilityInsuranceProperty from "./LiabilityInsuranceProperty"
import LiabilityInsuranceQuote from "./LiabilityInsuranceQuote"
import LiabilityInsuranceFinalize from "./LiabilityInsuranceFinalize"
import LiabilityInsuranceConfirmation from "./LiabilityInsuranceConfirmation"
import { LiabilityInsuranceTypes } from "@/types/liabilityInsuranceWizard"
import { AccountTypes } from "@/types/account"
import { Profile } from "@/types"

export interface LiabilityInsuranceContentProps {
  isLoading: boolean
  insuranceWizardData: LiabilityInsuranceTypes | undefined
  account: AccountTypes | undefined
  profile: Profile | undefined
}

const LiabilityInsuranceContent = ({
  isLoading,
  insuranceWizardData,
  account,
  profile,
}: LiabilityInsuranceContentProps) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const accessToken = useAccessToken()

  const { enabledSteps, setEnabledSteps } = useLiabilityInsuranceStore()

  const step = searchParams.get("step")

  const isLoggedIn = !!accessToken

  const handleNext = () => {
    const newActiveStep = Number(step) + 1
    const newEnabledSteps = [...enabledSteps]
    newEnabledSteps[Number(step)] = true
    searchParams.set("step", String(newActiveStep))

    setSearchParams(searchParams)
    setEnabledSteps(newEnabledSteps)
  }

  const handlePrevious = () => {
    searchParams.set("step", String(Number(step) - 1))

    setSearchParams(searchParams)
  }

  if (!isLoggedIn) {
    return <Navigate replace to="/getting-started" state={{ from: location }} />
  }

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Spinner />
      </div>
    )
  }

  if (!insuranceWizardData || !account || !profile) {
    return <Navigate replace to="/" />
  }

  if (insuranceWizardData?.completed && step !== "5") {
    return <Navigate replace to="/liability-insurance?step=5" />
  }

  if (!insuranceWizardData?.completed && step === "5") {
    return <Navigate replace to="/liability-insurance?step=1" />
  }

  if (step === null) {
    return <Navigate replace to="/liability-insurance?step=1" />
  }

  if (step === "1") {
    return <LiabilityInsuranceCoverage handleNext={handleNext} />
  }

  if (step === "2") {
    return (
      <LiabilityInsuranceProperty
        handlePrevious={handlePrevious}
        handleNext={handleNext}
        insuranceWizardData={insuranceWizardData}
      />
    )
  }

  if (step === "3") {
    return (
      <LiabilityInsuranceQuote
        handlePrevious={handlePrevious}
        handleNext={handleNext}
        insuranceWizardData={insuranceWizardData}
        account={account}
      />
    )
  }

  if (step === "4") {
    return (
      <LiabilityInsuranceFinalize
        handlePrevious={handlePrevious}
        insuranceWizardData={insuranceWizardData}
        account={account}
        profile={profile}
      />
    )
  }

  if (step === "5") {
    return (
      <LiabilityInsuranceConfirmation
        insuranceWizardData={insuranceWizardData}
      />
    )
  }
}

export default LiabilityInsuranceContent
